import { Country } from "@utils/constants/localisation";
import { getEnvTag, EnvTag } from '@utils/AppEnv';

export const createIntList = (start: number, len: number, step = 1) => (
  Array.from({ length: len }, (dummy, i) => start + (i * step))
);


// Abstract comparator covers null and undefined.
export const isNotNullNotUndefined = (variable: any): boolean => variable != null;


/**
 * Converts date object to string date, with the ISO format YYYY-MM-DD.
 * 
 * @param date Date object
 * @returns string date
 */
export const dateToISOFormat = (date: Date): string =>
  date.toLocaleDateString("fr-CA", { year: "numeric", month: "2-digit", day: "2-digit" });


/**
 * Converts date or string object to string date, with the ISO format DD MMM YYYY.
 * The caller must ensure an accepted format by Date constructor for the date
 * argument if it's a string.
 * 
 * @param date Date or String object
 * @returns string date in format DD MMM YYYY
 */
export const formatDateToDD_MMM_YYYY = (date: Date | string): string =>
  new Date(date).toLocaleDateString('en-GB', { year: 'numeric', month: 'short', day: 'numeric' });


/**
 * 
 * @param dateString string date expected in the ISO format of YYYY-MM-DD
 * @returns string date of DD-MM-YYYY format
 */
export const flipDateFormat = (dateString: string): string => {
  const [year, month, day] = dateString.split("-");
  return `${day}-${month}-${year}`;
};


/**
 * Function to check wether a given number, which should represent an year
 * is or is not a leap year.
 * 
 * @param year Number representing the year to be checked for being a leap year.
 * @returns Boolean representing whether the year is a leap year or not.
 */
const isLeapYear = (year: number): boolean => (year % 4 === 0 && year % 100 !== 0) || (year % 400 === 0);


/**
 * Function that takes a start date object and adds the weeks number that the next Y years have.
 * 
 * @param startDate Date object
 * @param numYears number of years, of whose number of weeks will be added to startDate 
 * @returns Date object
 */
export const addNextYearsWeeks = (startDate: Date, numYears: number): Date => {

  const endDate = new Date(startDate);
  let currentYear = startDate.getFullYear();
  let totalWeeks = 0;

  while (numYears > 0) {
    const daysInYear = isLeapYear(currentYear) ? 366 : 365;
    totalWeeks += daysInYear / 7;

    numYears--;
    currentYear++;
  }

  const daysToAdd = Math.ceil(totalWeeks) * 7;
  endDate.setDate(endDate.getDate() + daysToAdd);

  return endDate;
};


/**
 * Function that takes a date object and adds a given the years number that the next Y years have.
 * There is a argument to the function to instruct that under certain cases for leap years, to
 * forward the end day, which in the edge cases it would be Feb 29th, to Mar 1st.
 * 
 * As leap years represent the edge cases, it's worth providing a detailed view on how they are
 * treated, alongside with examples. For the following explanation on how the function considers
 * leap years, the input day will be considered as being last day of Feb - either 28 or 29 on
 * leap years.
 * 1) Both input and end year ARE NOT leap year
 *  Input and end dates will be Feb 28th, y years apart, represented by the 2nd input argument.
 * 
 * 2) Both input and end year ARE leap year
 *  As the input date will be in this case Feb 29th, the output date will be Feb 29th, y years
 * apart, represented by the 2nd input argument.
 * 
 * 3) Input year IS NOT leap year, output year IS leap year
 *  This means the input date is a Feb 28th, which will give an output date of Feb 28th, the two
 * dates being Y years apart from each other.
 * 
 * 4) Input year IS leap year, output year IS NOT leap year
 * This case is a little more complex. The input date will be a Feb 29th. Depending on the case,
 * the output may be Feb 28th or Mar 1st. With this scope, a 3rd function argument was introduced.
 * It's meant to be explicitly used in those cases when it is wanted to forward the end date to
 * Mar 1st, as the default behavior is to go for Feb 28th.
 * 
 * @param date Date object
 * @param numYears number of years to add to with respect to date parameter 
 * @param onFeb29OvfToMarch boolean to indicate that Feb 29th end dates should be forwarded to Mar 1st
 * @returns Date object
 */
export const addYearsToDate = (date: Date, numYears: number, onFeb29OvfToMarch: boolean = false): Date => {
  const startYear = date.getFullYear();
  const startMonth = date.getMonth();
  const startDay = date.getDate();
  
  const output: Date = new Date(date);

  let endDay = startDay;
  let endMonth = startMonth;
  const endYear = startYear + numYears;

  // For a start day of Feb 29th (automatically the input year
  // being a leap year), set output day as 29 (same as input).
  if (isLeapYear(endYear)){
    if (startMonth === 1 && startDay === 29){
      endDay = 29;
    }
  // If start year is leap year, but end year is not, then
  // depending on input, roll to March.
  } else {
    if (isLeapYear(startYear) && !isLeapYear(endYear)){
      if (startMonth === 1 && startDay === 29){
        if (onFeb29OvfToMarch) {
          endDay = 1;
          endMonth = 2;
        } else {
          endDay = 28;
          endMonth = 1;
        }
      }
    }  
  }

  output.setFullYear(endYear, endMonth, endDay);
  return output;
};


/**
 * Function that takes a start date object and adds the number of weeks to get the end date
 * 
 * @param startDate Date object
 * @param numWeeks number of weeks, wherein number of weeks will be added to startDate 
 * @returns Date object
 */
export const addWeeks = (startDate: Date, numWeeks: number): Date => {
  const endDate = new Date(startDate);
  const daysToAdd = Math.ceil(numWeeks) * 7;
  endDate.setDate(endDate.getDate() + daysToAdd);
  return endDate;
}


/**
 * Function that takes a start date object and adds the number of days to get the end date
 * 
 * @param startDate Date object
 * @param numDays number of days to add to startDate
 * @returns Date object
 */
export const addDays = (startDate: Date, numDays: number): Date => {
  const endDate = new Date(startDate);
  endDate.setDate(endDate.getDate() + numDays );
  return endDate;
}


/**
 * Function that takes a start date object and adds the number of weeks to get the end date
 * 
 * @param dateString Date as a string
 * @param numWeeks number of weeks, wherein number of weeks will be added to startDate 
 * @returns String object
 */
export const calculateFutureDate = (dateString: string, weeks: number): string => {
  const date = new Date(dateString);
  if (isNaN(date.getTime())) {
      throw new Error('Invalid date string');
  }
  date.setDate(date.getDate() + weeks * 7);
  return date.toISOString().split('T')[0];
};

export const getTimeDifference = (startDate: Date, endDate: Date) => {
  const timeDifferenceInMilliseconds: number = endDate.getTime() - startDate.getTime();

  const hours: number = Math.floor(timeDifferenceInMilliseconds / (1000 * 60 * 60));
  const remainingMillisecondsAfterHours: number = timeDifferenceInMilliseconds % (1000 * 60 * 60);

  const minutes: number = Math.floor(remainingMillisecondsAfterHours / (1000 * 60));
  const remainingMillisecondsAfterMinutes: number = remainingMillisecondsAfterHours % (1000 * 60);

  const seconds: number = Math.floor(remainingMillisecondsAfterMinutes / 1000);

  const formattedHours = hours.toString();
  const formattedMinutes = minutes.toString();
  const formattedSeconds = seconds.toString();

  return `${formattedHours} hours, ${formattedMinutes} minutes, ${formattedSeconds} seconds`;
};


export const countrySpecificCurrency = (country: string): string => 
  country === Country.AU ? "$" : "£";


export const countrySpecificDistanceUnit = (country: string): string => 
  country === Country.AU ? "km" : "miles";


//Matches only on the name returned from zoho user email as some users log into smart ops with a .com email
// but have a .com.au email stored in Zoho
export const emailMinusSplendDomain = (email: string) => {
  const emailString = email.toLowerCase();
  if (emailString.includes('splend.com')) {
    let splitEmail = emailString.split('@')[0];
    splitEmail = splitEmail.replace(/-uk|\.uk/g, '')
    splitEmail = splitEmail.replace(/-au|\.au/g, '')
    return splitEmail;
  }
  return email;
};



//Checks if the ending kms for the cancellation/test drive is greater than or equal to value
//of odometer in fleetio. This is because Fleetio update will fail if the input value is less than
//the odometer value in Fleetio
export const checkIfValidFleetioOdometerUpdate = (minValue: string, inputValue: string) =>
  parseFloat(inputValue) < parseFloat(minValue) ? false : true;


/**
 * Function to preserve just the digits from an alpha-numeric string.
 * 
 * @param value string representation of generic value
 * @returns string representation of only the digits from the input string
 */
export const keepDigits = (value: string): string => {
  value = value.replace(/[^0-9]/g, '');
  return value;
};


/**
 * Function to round a value to to 2dp
 * 
 * @param num string representation of generic value
 * @returns number rounded to 2 decimal places
 */
export const truncateToTwoDecimals = (num: string | number): number => {
  const parsedNum = typeof num === "string" ? parseFloat(num) : num;
  if (isNaN(parsedNum)) {
    throw new Error("Input must be a valid number or a string representing a number.");
  }
  return Math.floor(parsedNum * 100) / 100;
};

/**
 * Function to round a value to to 2dp
 *
 * @param value string or number to be converted
 * @returns number rounded to 2 decimal places or if 0 a string rounded to 2 dp
 */
export const formatCurrency = (value: number | string): string => {
  if (value === 0 || value === '0') {
      return '0.00'; // Show 0 explicitly
  }
  const numericValue = Number(value);
  return !isNaN(numericValue) ? numericValue.toFixed(2) : ''; // Format with 2 decimal places or return empty
};

/**
 * Converts pence/cents to pounds/dollars
 *
 * @param value The value in pence/cents (minor currency unit)
 * @returns The value in pounds/dollars (major currency unit)
 */
export const convertMinorToMajorCurrency = (value: number | string): number => {
  const numericValue = typeof value === "string" ? parseFloat(value) : value;
  if (isNaN(numericValue)) {
    return 0;
  }
  return numericValue / 100;
};

/**
 * Converts pence/cents to pounds/dollars
 *
 * @param value The value in pence/cents (minor currency unit)
 * @returns The value in pounds/dollars (major currency unit)
 */
export const convertMajorToMinorCurrency = (value: number | string): number => {
  const numericValue = typeof value === "string" ? parseFloat(value) : value;
  if (isNaN(numericValue)) {
    return 0;
  }
  return numericValue * 100;
};

/**
 * Function to get link to Chargebee payment method depending on the country and env
 * 
 * @param custId Zoho Customer ID
 * @param country Country to be generate link for
 * @returns link to chargebee payment method for specific customer
 */
export const getChargebeePaymentMethodsLink = (custId: string, country: string) => {
  const chargebeeLinks = {
      GB: {
          prod: `https://splend-uk.chargebee.com/d/customers/${custId}#payment-method`,
          dev: `https://splend-uk-test.chargebee.com/d/customers/${custId}#payment-method`
      },
      AU: {
          prod: `https://splend-au.chargebee.com/d/customers/${custId}#payment-method`,
          dev: `https://splend-au-test.chargebee.com/d/customers/${custId}#payment-method`
      }
  };

  const env = getEnvTag() === EnvTag.Production ? "prod" : "dev";
  return country === Country.AU ? chargebeeLinks.AU[env] : chargebeeLinks.GB[env];
};


/**
 * Get a zoho create potential link - depending on environment
 */
export const getZohoCreatePotentialLink = (country: Country): string => {
   const currEnvTag = getEnvTag();
    switch (currEnvTag) {
        case EnvTag.Production:
            switch (country) {
                case Country.GB:
                    return 'https://crm.zoho.com/crm/org658091538/tab/Potentials/create?layoutId=2921657000137183001&redirect=false';
                default:
                    //AU
                    return 'https://crm.zoho.com/crm/org684098423/tab/Potentials/create?layoutId=3825483000196705001&redirect=false';
            }
            break;
        default:
            //Sandbox's
            switch (country) {
                case Country.GB:
                    return 'https://crmsandbox.zoho.com/crm/smartopsk/tab/Potentials/create?layoutId=4992872000001170185&redirect=false';
                default:
                    //AU
                    return 'https://crmsandbox.zoho.com/crm/smartops/tab/Potentials/create?layoutId=5030386000199602273&redirect=false'
            }
    }
}

export const getZohoContactLink = (country: Country, customerId: string): string => {
  const currEnvTag = getEnvTag();
  
  switch (currEnvTag) {
      case EnvTag.Production:
          switch (country) {
              case Country.GB:
                  return `https://crm.zoho.com/crm/org658091538/tab/Contacts/${customerId}`;
              default:
                  // AU
                  return `https://crm.zoho.com/crm/org684098423/tab/Contacts/${customerId}`;
          }
      default:
          // Sandbox
          switch (country) {
              case Country.GB:
                  return `https://crmsandbox.zoho.com/crm/smartopsk/tab/Contacts/${customerId}`;
              default:
                  // AU
                  return `https://crmsandbox.zoho.com/crm/smartops/tab/Contacts/${customerId}`;
          }
  }
};

export const getZohoPotentialLink = (country: Country, potentialId: string): string => {
  const currEnvTag = getEnvTag();
  
  switch (currEnvTag) {
      case EnvTag.Production:
          switch (country) {
              case Country.GB:
                  return `https://crm.zoho.com/crm/org658091538/tab/Potentials/${potentialId}`;
              default:
                  // AU
                  return `https://crm.zoho.com/crm/org684098423/tab/Potentials/${potentialId}`;
          }
      default:
          // Sandbox
          switch (country) {
              case Country.GB:
                  return `https://crmsandbox.zoho.com/crm/smartopsk/tab/Potentials/${potentialId}`;
              default:
                  // AU
                  return `https://crmsandbox.zoho.com/crm/smartops/tab/Potentials/${potentialId}`;
          }
  }
};


/**
 * @returns time as string containing only hours and minutes formatted as HH:MM
 */
export const currentTime_HHMM = (): string => {
  const now = new Date();
  const twoDigitsHour = String(now.getHours()).padStart(2, '0');
  const twoDigitsMin = String(now.getMinutes()).padStart(2, '0');
  return `${twoDigitsHour}:${twoDigitsMin}`;
};

/**
 * @returns end of day time as string, as constant, containing only hours and minutes formatted as HH:MM
 */
export const endOfDayTime_HHMM = (): string => '23:59';

/**
 * @returns UTC datetime in the form (YYYY-MM-DDTHH:MM:SS.msZ) as datetime in local timezone 
 */
export const utcDateTimeExpressedInLocalTimeZone = (
  utcDateTime: string | undefined | null,
  dateStyle?: "full" | "long" | "medium" | "short" | undefined,
  timeStyle?: "full" | "long" | "medium" | "short" | undefined
) => {
  if (!utcDateTime) return "";
  return (
    new Intl.DateTimeFormat(
      navigator.language,
      {
        dateStyle: dateStyle,
        timeStyle: timeStyle,
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone
      }
    ).format(new Date(utcDateTime))
  )
}

/**
 * Utility function to extract the root group from a (Fleetio) group_ancestry string.
 * E.g. "NSW / Sydney" => "NSW"
 * @param groupAncestry - the group ancestry string
 * @returns the root group (before the first '/')
 */
export function getRootGroupFromAncestry(groupAncestry: string): string {
  if (!groupAncestry) return '';
  return groupAncestry.split('/')[0].trim();
}
